import { FeatureFlags } from '~/modules/feature_flags';
import {
    SPONSORED_EXPLORE_REGEX,
    SPONSORED_SEARCH_REGEX,
    hasKeyword,
    isAppExchange,
    isExploreBucketUrl,
    isKeywordlessSearch,
    isLearn,
    urlContainsFacebookParameter,
    urlContainsGoogleParameter,
    urlContainsLinkedInParameter,
    urlContainsTwitterParameter,
    urlIsFacebook,
    urlIsGoogle,
    urlIsKeywordlessSearch,
    urlIsLinkedIn,
    urlIsSearchPage,
    urlIsTwitter,
} from '~/modules/utilities/channelUtilities';

/* Please read the below note before making any changes. The below channel attribution logic
 * has been replicated in the analytics_cleaner repo for legacy site events.
 * https://github.com/OneAppExchange/analytics-cleaner/blob/main/src/stages/channel.service.ts
 * Please ensure this logic is aligned in both the places.
 */

const isSponsored = (url: string, sponsored: boolean, recommended: boolean) => {
    return (/channel=featured/.test(url) && !recommended) || sponsored;
};

const isSponsoredSearch = (url: string, sponsoredSearch: boolean, recommended: boolean) => {
    return (SPONSORED_SEARCH_REGEX.test(url) || (sponsoredSearch && !urlIsKeywordlessSearch(url))) && !recommended;
};

const isSponsoredExplore = (url: string, sponsoredSearch: boolean, recommended: boolean) => {
    return (
        (SPONSORED_EXPLORE_REGEX.test(url) ||
            (sponsoredSearch && (urlIsKeywordlessSearch(url) || isExploreBucketUrl(url)))) &&
        !recommended
    );
};

const isExplore = (url: string, referrer: string) => {
    return (
        isAppExchange(referrer) &&
        (isKeywordlessSearch(url, referrer) ||
            isExploreBucketUrl(url) ||
            (isExploreBucketUrl(referrer) && !urlIsSearchPage(url)))
    );
};

const isWeb = (url: string) => {
    if (!url) {
        return false;
    }

    const { searchParams } = new URL(url);
    const socParam = searchParams.get('soc');

    return socParam && /^amp/.test(socParam);
};

const isGoogle = (url: string, referrer: string) => {
    return urlIsGoogle(referrer) || urlContainsGoogleParameter(referrer) || urlContainsGoogleParameter(url);
};

const isFacebook = (url: string, referrer: string) => {
    return urlIsFacebook(referrer) || urlContainsFacebookParameter(referrer) || urlContainsFacebookParameter(url);
};

const isLinkedIn = (url: string, referrer: string) => {
    return urlIsLinkedIn(referrer) || urlContainsLinkedInParameter(referrer) || urlContainsLinkedInParameter(url);
};

const isTwitter = (url: string, referrer: string) => {
    return urlIsTwitter(referrer) || urlContainsTwitterParameter(referrer) || urlContainsTwitterParameter(url);
};

const isAppexchangeSearch = (url: string, referrer: string) => {
    const searchUrl = urlIsSearchPage(url);

    return (
        isAppExchange(referrer) &&
        ((searchUrl && hasKeyword(url)) || (!searchUrl && urlIsSearchPage(referrer) && hasKeyword(referrer)))
    );
};

const isAppExchangeLearn = (url: string, referrer: string) => {
    return (isAppExchange(referrer) && isLearn(referrer)) || (isAppExchange(url) && isLearn(url));
};

const isRecommended = (url: string, referrer: string, recommended: boolean) => {
    return /channel=recommended/.test(url) || recommended;
};

const isCategory = (referrer: string) => {
    const regex = [
        /\/appxStore\?type=/,
        /\/mktcollections\/cloud-collections/,
        /\/mktcollections\/industry-collections/,
        /\/category\//,
    ];

    return regex.some((r) => r.test(referrer));
};

export const getChannel = (
    url: string,
    referrer: string,
    sponsored: boolean,
    recommended: boolean,
    sponsoredSearch: boolean,
) => {
    if (referrer && !/^https?:\/\//.test(referrer)) {
        referrer = `http://${referrer}`;
    }

    switch (true) {
        case isSponsoredExplore(url, sponsoredSearch, recommended):
            return { channel: 'AppExchange Sponsored Explore', parent_channel: 'AppExchange' };
        case isSponsoredSearch(url, sponsoredSearch, recommended):
            return { channel: 'AppExchange Sponsored Search', parent_channel: 'AppExchange' };
        case isSponsored(url, sponsored, recommended):
            return { channel: 'AppExchange Featured', parent_channel: 'AppExchange' };
        case isRecommended(url, referrer, recommended):
            return { channel: 'AppExchange Recommended', parent_channel: 'AppExchange' };
        case isExplore(url, referrer):
            return { channel: 'AppExchange Explore', parent_channel: 'AppExchange' };
        case isAppexchangeSearch(url, referrer):
            return { channel: 'AppExchange Search', parent_channel: 'AppExchange' };
        case isCategory(referrer):
            return { channel: 'AppExchange Categories', parent_channel: 'AppExchange' };
        case FeatureFlags.FF_MA_NEW_TRAFFIC_SOURCES && isAppExchangeLearn(url, referrer):
            return { channel: 'AppExchange Learn', parent_channel: 'AppExchange' };
        case isGoogle(url, referrer):
            return { channel: 'Google', parent_channel: 'Google' };
        case isFacebook(url, referrer):
            return { channel: 'Facebook', parent_channel: 'Facebook' };
        case FeatureFlags.FF_MA_NEW_TRAFFIC_SOURCES && isLinkedIn(url, referrer):
            return { channel: 'LinkedIn', parent_channel: 'LinkedIn' };
        case FeatureFlags.FF_MA_NEW_TRAFFIC_SOURCES && isTwitter(url, referrer):
            return { channel: 'X (Twitter)', parent_channel: 'X (Twitter)' };
        case isWeb(url):
            return { channel: 'Web', parent_channel: 'Web' };
        case isAppExchange(referrer):
            return { channel: 'AppExchange Browse', parent_channel: 'AppExchange' };
        default:
            return { channel: 'Web', parent_channel: 'Web' };
    }
};
